import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { tApi } from "@/lib/api";

// Percorsi che NON sono centri
const RESERVED = new Set([
  "admin", "api", "app", "staff", "login", "register", "assets", "static", "consenso",
  // pagine legali: non possono essere scambiate per lo slug di un centro
  "privacy", "cookie-policy", "termini", "intelligenza-artificiale", "note-legali",
]);

/**
 * Manifest PWA per centro.
 *
 * Il manifest statico ha start_url alla radice: chi salva sulla home dalla
 * pagina di un centro (es. /odyneai/login) si ritroverebbe ad aprire il sito
 * principale. Qui generiamo al volo un manifest con start_url e scope del
 * centro corrente, così l'icona salvata apre direttamente il suo salone.
 * Gli URL sono ASSOLUTI perché il manifest vive su un blob: e i percorsi
 * relativi non si risolverebbero correttamente.
 */
export default function DynamicManifest() {
  const { pathname } = useLocation();

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;

    const m = pathname.match(/^\/([a-zA-Z0-9-]+)(?:\/|$)/);
    const slug = m && !RESERVED.has(m[1].toLowerCase()) ? m[1].toLowerCase() : null;

    // Fuori dai centri (landing, admin): manifest standard del sito
    if (!slug) {
      link.setAttribute("href", "/manifest.json");
      return;
    }

    let blobUrl = null;
    let annullato = false;

    const applica = (nomeCentro) => {
      if (annullato) return;
      const origin = window.location.origin;
      const nome = nomeCentro || slug;
      const manifest = {
        name: `${nome} — AURA`,
        short_name: nome,
        description: `Prenota i tuoi trattamenti da ${nome}.`,
        start_url: `${origin}/${slug}/app`,
        scope: `${origin}/${slug}/`,
        display: "standalone",
        theme_color: "#1A1A1A",
        background_color: "#FAFAFA",
        icons: [
          { src: `${origin}/icon-192.png`, type: "image/png", sizes: "192x192" },
          { src: `${origin}/icon-512.png`, type: "image/png", sizes: "512x512" },
        ],
      };
      const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
      blobUrl = URL.createObjectURL(blob);
      link.setAttribute("href", blobUrl);

      // iOS: nome sotto l'icona quando si aggiunge alla schermata Home
      let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "apple-mobile-web-app-title");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", nome);
    };

    // Nome del centro per l'etichetta dell'icona (fallback: lo slug)
    tApi(slug).publicInfo().then((info) => applica(info?.name)).catch(() => applica(null));

    return () => {
      annullato = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [pathname]);

  return null;
}
